export type PromotionType =
  | 'percentage_discount'
  | 'fixed_amount'
  | 'bxgy'
  | 'flash_sale'
  | 'bundle'
  | 'tier_discount'
  | 'voucher'
  | 'free_shipping'
  | 'wholesale_gift'
  | 'gold_price_lock';
export type PromotionStatus = 'draft' | 'pending' | 'active' | 'paused' | 'expired' | 'cancelled' | 'inactive';

export interface TenantPromotion {
  _id: string;
  code: string;
  name: string;
  type: PromotionType;
  value: number;
  minOrder?: number;
  maxDiscount?: number;
  startAt: string;
  endAt: string;
  usageLimit?: number;
  usageCount: number;
  applicableKarat?: string[];
  status: PromotionStatus;
  note?: string;
}

export interface TenantVoucher {
  _id: string;
  code: string;
  promotionId: string;
  promotionName: string;
  customerPhone?: string;
  value: number;
  used: boolean;
  usedAt?: string;
  expiresAt: string;
  createdAt: string;
}

export interface FlashSale {
  _id: string;
  name: string;
  startAt: string;
  endAt: string;
  discountPct: number;
  products: Array<{ sku: string; name: string; originalPrice: number; salePrice: number; stock: number }>;
  status: PromotionStatus;
}

export const PROMOTION_TYPE_LABEL: Record<PromotionType, string> = {
  percentage_discount: 'Giảm %',
  fixed_amount: 'Giảm tiền',
  bxgy: 'Mua X tặng Y',
  flash_sale: 'Flash Sale',
  bundle: 'Combo',
  tier_discount: 'Thành viên',
  voucher: 'Voucher',
  free_shipping: 'Miễn phí ship',
  wholesale_gift: 'Quà sỉ',
  gold_price_lock: 'Khoá giá vàng',
};

export const PROMOTION_STATUS_LABEL: Record<PromotionStatus, string> = {
  draft: 'Nháp',
  pending: 'Chờ duyệt',
  active: 'Đang chạy',
  paused: 'Tạm dừng',
  expired: 'Hết hạn',
  cancelled: 'Đã huỷ',
  inactive: 'Chưa kích hoạt',
};

export const PROMOTION_STATUS_COLOR: Record<PromotionStatus, string> = {
  draft: 'default',
  pending: 'blue',
  active: 'green',
  paused: 'orange',
  expired: 'red',
  cancelled: 'red',
  inactive: 'default',
};

const baseDate = (offset: number) => {
  const d = new Date(2026, 3, 1);
  d.setDate(d.getDate() + offset);
  return d.toISOString();
};

export const mockPromotions: TenantPromotion[] = [
  {
    _id: 'pr-01', code: 'TANG5PC', name: 'Giảm 5% toàn đơn cuối tuần',
    type: 'percentage_discount', value: 5, minOrder: 10_000_000,
    startAt: baseDate(-10), endAt: baseDate(20),
    usageCount: 42, usageLimit: 500,
    status: 'active',
  },
  {
    _id: 'pr-02', code: 'SJC-500K', name: 'Giảm 500K SJC ≥ 5 chỉ',
    type: 'fixed_amount', value: 500_000, minOrder: 40_000_000,
    applicableKarat: ['SJC'], startAt: baseDate(-20), endAt: baseDate(10),
    usageCount: 18, usageLimit: 100,
    status: 'active',
  },
  {
    _id: 'pr-03', code: 'BOGO-18K', name: 'Mua nhẫn 18K tặng bông tai bạc',
    type: 'bxgy', value: 1, applicableKarat: ['18K'],
    startAt: baseDate(-5), endAt: baseDate(25),
    usageCount: 7,
    status: 'active',
  },
  {
    _id: 'pr-04', code: 'BIRTHDAY-X2', name: 'Nhân đôi điểm sinh nhật khách VIP',
    type: 'tier_discount', value: 2,
    startAt: baseDate(-40), endAt: baseDate(-15),
    usageCount: 23,
    status: 'expired',
  },
  {
    _id: 'pr-05', code: 'NEWYEAR2027', name: 'Giảm 10% đầu năm — sắp triển khai',
    type: 'percentage_discount', value: 10, minOrder: 5_000_000,
    startAt: baseDate(180), endAt: baseDate(210),
    usageCount: 0, usageLimit: 1000,
    status: 'draft',
  },
];

export const mockVouchers: TenantVoucher[] = [
  {
    _id: 'vc-01', code: 'VIP-A8XK2P', promotionId: 'pr-01', promotionName: 'Giảm 5% toàn đơn',
    customerPhone: '0901234001', value: 500_000,
    used: false, expiresAt: baseDate(20), createdAt: baseDate(-10),
  },
  {
    _id: 'vc-02', code: 'VIP-B9YL3Q', promotionId: 'pr-02', promotionName: 'Giảm 500K SJC',
    customerPhone: '0901234003', value: 500_000,
    used: true, usedAt: baseDate(-5), expiresAt: baseDate(10), createdAt: baseDate(-20),
  },
  {
    _id: 'vc-03', code: 'NEW-C1ZM4R', promotionId: 'pr-01', promotionName: 'Giảm 5% toàn đơn',
    value: 1_000_000, used: false, expiresAt: baseDate(25), createdAt: baseDate(-3),
  },
  {
    _id: 'vc-04', code: 'GIFT-D2AN5S', promotionId: 'pr-04', promotionName: 'Nhân đôi điểm sinh nhật',
    customerPhone: '0901234009', value: 200_000,
    used: true, usedAt: baseDate(-20), expiresAt: baseDate(-15), createdAt: baseDate(-40),
  },
];

export const mockFlashSales: FlashSale[] = [
  {
    _id: 'fs-01', name: 'Flash Sale thứ 7 — 18K',
    startAt: baseDate(2), endAt: baseDate(2),
    discountPct: 8,
    products: [
      { sku: 'NH18K-A01', name: 'Nhẫn cưới 18K #A01', originalPrice: 5_400_000, salePrice: 4_968_000, stock: 5 },
      { sku: 'BT18K-C12', name: 'Bông tai 18K #C12', originalPrice: 1_600_000, salePrice: 1_472_000, stock: 10 },
    ],
    status: 'draft',
  },
];
